import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Copy, Check, KeyRound, ArrowUpRight } from "lucide-react";
import * as api from "../../lib/api";
import { Card, Label, Hint, inputCls, ringStyle } from "./shared";

// Integration keys are saved on their own (POST/DELETE /settings/integration-keys),
// so this section has no Save button. One key per consumer, each with only the
// scopes it needs; the raw key is shown once, right after it is created.
const PRESETS = [
  { id: "agent", label: "AI agent (MCP)", name: "AI agent", scopes: ["mcp", "fanout:seed", "sitemap:crawl"] },
  { id: "agent-read", label: "AI agent, read-only (MCP)", name: "AI agent (read-only)", scopes: ["mcp:read"] },
  { id: "youtube", label: "YouTube app (read-only export)", name: "YouTube app", scopes: ["export:read"] },
  { id: "telegram", label: "Telegram / Hermes webhook", name: "Telegram webhook", scopes: ["fanout:seed"] },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "never");

function NewKeyBanner({ created, onDone }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(created.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2">
      <p className="text-xs font-bold text-zinc-900">Copy the key for "{created.name}" now — it won't be shown again.</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 break-all text-[11px] font-mono bg-white border border-amber-100 rounded px-2 py-1.5">{created.key}</code>
        <button type="button" onClick={copy} className="text-zinc-500 hover:text-zinc-900" title="Copy">
          {copied ? <Check size={16} strokeWidth={2} className="text-green-600" /> : <Copy size={16} strokeWidth={2} />}
        </button>
      </div>
      <button type="button" onClick={onDone} className="text-[11px] text-zinc-500 underline underline-offset-4">I've stored it</button>
    </div>
  );
}

export default function IntegrationsSection() {
  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState(PRESETS[0].id);
  const [name, setName] = useState(PRESETS[0].name);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  const load = () =>
    api.listIntegrationKeys()
      .then(r => { setKeys(r.keys); setScopes(r.scopes); setError(null); })
      .catch(err => setError(err.response?.data?.detail || "Couldn't load integration keys."))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const pickPreset = (id) => {
    setPreset(id);
    setName(PRESETS.find(p => p.id === id).name);
  };

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const p = PRESETS.find(x => x.id === preset);
      setCreated(await api.createIntegrationKey({ name: name.trim() || p.name, scopes: p.scopes }));
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't create the key.");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using this key stops working at once. Other keys are not affected.`)) return;
    try {
      await api.revokeIntegrationKey(k.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't revoke the key.");
    }
  };

  return (
    <div className="space-y-4">
      <Card icon={KeyRound} title="Integration Keys" subtitle="One key per external app, each limited to what it needs">
        {created && <NewKeyBanner created={created} onDone={() => setCreated(null)} />}

        <div>
          <Label>Active keys</Label>
          {loading ? (
            <p className="text-xs text-zinc-400">Loading…</p>
          ) : keys.length === 0 ? (
            <p className="text-xs text-zinc-400">No keys yet.</p>
          ) : (
            <ul className="divide-y divide-zinc-100 border border-zinc-100 rounded-lg">
              {keys.map(k => (
                <li key={k.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-zinc-900 truncate">
                      {k.name} <span className="font-mono font-normal text-zinc-400">{k.key_prefix}…</span>
                    </p>
                    <p className="text-[10px] text-zinc-500">
                      {k.scopes.join(", ")} · created {fmtDate(k.created_at)} · last used {fmtDate(k.last_used_at)}
                    </p>
                  </div>
                  <button type="button" onClick={() => handleRevoke(k)}
                    className="shrink-0 px-2.5 py-1 text-[11px] border border-zinc-200 text-red-600 hover:bg-red-50 rounded-md"
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
          {keys.some(k => k.scopes.includes("api:full")) && (
            <Hint>A key with api:full can call every dashboard route as an admin. Replace it with a narrower key, then revoke it.</Hint>
          )}
        </div>

        <div className="space-y-2">
          <Label>New key</Label>
          <select value={preset} onChange={e => pickPreset(e.target.value)} className={inputCls} style={ringStyle}>
            {PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <input value={name} onChange={e => setName(e.target.value)} maxLength={80} placeholder="Name, e.g. Claude Code on my laptop"
            className={inputCls} style={ringStyle}
          />
          <Hint>
            Scopes: {PRESETS.find(p => p.id === preset).scopes.map(s => `${s} — ${scopes[s] || ""}`).join("; ")}
          </Hint>
          <button type="button" onClick={handleCreate} disabled={creating}
            className="px-3 py-2 text-xs font-bold border border-zinc-100 text-zinc-900 hover:bg-zinc-50 disabled:opacity-40 transition-colors rounded-lg"
          >
            {creating ? "Creating..." : "Create key"}
          </button>
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        <Link to="/dashboard/agents"
          className="inline-flex items-center gap-1 text-xs text-zinc-500 underline underline-offset-4 decoration-zinc-300 hover:text-zinc-900"
        >
          Connect an AI agent in Agent Setup <ArrowUpRight size={12} />
        </Link>
      </Card>

      <Card icon={Bot} title="Fan-Out Webhook" subtitle="Trigger fan-out with a key that has the fanout:seed scope">
        <div className="bg-zinc-50 border border-zinc-100 rounded-lg px-3 py-2.5 space-y-1 text-[10px] text-zinc-500 font-mono break-all">
          <p>POST {import.meta.env.VITE_BACKEND_URL}/api/fanout/telegram-seed</p>
          <p>Header: X-Integration-Key: &lt;key&gt;</p>
          <p>Body: {"{"} "seed_topic": "...", "mode": "simple" {"}"}</p>
        </div>
        <Hint>
          Runs the full fan-out pipeline (expand → classify → cluster-match → save → push gaps to Recommendations) for a seed topic.
        </Hint>
      </Card>

      <Card icon={Bot} title="MCP Server" subtitle="For ChatGPT, Hermes and other MCP clients — needs a key with the mcp scope">
        <div className="bg-zinc-50 border border-zinc-100 rounded-lg px-3 py-2.5 space-y-1 text-[10px] text-zinc-500 font-mono break-all">
          <p>URL: {import.meta.env.VITE_BACKEND_URL}/mcp</p>
          <p>Header: X-Integration-Key: &lt;key&gt;</p>
        </div>
        <Hint>
          Tools: <span className="font-mono">diagnose_article</span> (18-dim Article Intelligence score), <span className="font-mono">rewrite_article</span> (OpenAI rewrite targeting only weak dimensions, not applied), <span className="font-mono">apply_article_rewrite</span> (pushes a reviewed rewrite live and records a before/after GSC snapshot) — plus <span className="font-mono">write_copy</span>/<span className="font-mono">optimize_copy</span>/<span className="font-mono">push_copy_draft</span> for net-new copy.
        </Hint>
        <Hint>
          E.g. ask the agent: "Diagnose article 412, then rewrite it for the keyword 'office cleaning service jakarta', then apply it." <span className="font-medium text-zinc-500">Changes to the live site need an approved task</span> — the agent files a task, and it can apply only after someone on your team moves that task to In progress on the Tasks board.
        </Hint>
      </Card>
    </div>
  );
}
